import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchDatGeneral, deleteDatGeneral } from '../../reducers/datGeneral_slice';
import DatGeneralForm from './DatGeneralForm';
import Table from 'react-bootstrap/Table';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import CustomPrintModalH from '../UI/CustomPrintModalH';
import { fechaHoraBdAjustada } from '../recibo/ImprimirRecibo';

const DatGeneralList = () => {
  const dispatch = useDispatch();
  const datGeneralState = useSelector(state => state.datgeneral || { datgeneralList: [] });
  const datGenerals = datGeneralState.datgeneralList || [];
  const [showModal, setShowModal] = useState(false);
  const [currentDatGeneral, setCurrentDatGeneral] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchDatGeneral());
  }, [dispatch]);

  const handleDelete = id => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "¡No podrás revertir esto!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#464646',
      confirmButtonText: 'Sí, eliminarlo!',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteDatGeneral(id)).unwrap().then(() => {
          Swal.fire('¡Eliminado!', 'El registro ha sido eliminado.', 'success');
        }).catch((error) => {
          Swal.fire('Error', error.error || 'Ocurrió un error al eliminar el registro', 'error');
        });
      }
    });
  };

  const handleSort = (key) => {
    if (sortConfig.direction === 'desc') {
      setSortConfig({ key: null, direction: 'asc' });
      return;
    }

    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = null;
    }

    setSortConfig({ key, direction });
  };

  const handleEdit = datGeneral => {
    setCurrentDatGeneral(datGeneral);
    setShowModal(true);
  };

  const handleCreate = () => {
    setCurrentDatGeneral(null);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setCurrentDatGeneral(null);
    dispatch(fetchDatGeneral());
  };

  const handleBack = () => {
    navigate('/admin');
  };

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(sortedDatGenerals);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "DatosGenerales");
    XLSX.writeFile(workbook, "datos_generales.xlsx");
  };

  const sortedDatGenerals = useMemo(() => {
    let sortedData = [...datGenerals];
    if (sortConfig.key) {
      sortedData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortedData;
  }, [datGenerals, sortConfig]);

  const handleShowPreview = () => {
    setPrintContent(getPreviewContent());
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
  };

  const getPreviewContent = () => {
    const content = {
      head: [['N°', 'Últ. Modificación', 'Nombre Colegio', 'Dirección', 'Teléfono', 'Email', 'Representante', 'Cuenta CAT', 'Cuenta CAB', 'Cuenta Visado', 'Tolerancia']],
      body: sortedDatGenerals.map((dat, index) => [
        index + 1,
        dat.updatedAt ? fechaHoraBdAjustada(dat.updatedAt).completa : '30/06/2025 16:00:00',
        dat.nombre_colegio,
        dat.direccion,
        dat.telefono,
        dat.email,
        dat.representante,
        dat.cuenta_CAT,
        dat.cuenta_CAB,
        dat.cuenta_visado,
        `${dat.cuotas_tolerancia} cuota(s)`,
      ])
    };
    return content;
  };

  return (
    <div className="list-container list1">
      <h1 className='table-title'>Datos Generales</h1>
      <div className="list-content">
        <div className="list-container-header">
          <div className="left-buttons">
            <button title="Volver" onClick={handleBack} className="icon-button btn-transparent"><i className="bi bi-arrow-left"></i></button>
          </div>
          <div className="right-buttons">
            <button title="Imprimir" onClick={handleShowPreview} className="icon-button btn-transparent"><i className="bi bi-printer"></i></button>
            <button title="Exportar a Excel" onDoubleClick={handleExport} className="icon-button btn-transparent"><i className="bi bi-file-spreadsheet"></i></button>
          </div>
        </div>
        {datGenerals.length === 0 ? (
          <p>No hay datos generales disponibles.</p>
        ) : (
          <div className='table-container'>
            <Table bordered hover size='sm'>
              <thead style={{ backgroundColor: "#b2aea3" }}>
                <tr>
                  <th className='col-index'>N°</th>
                  <th>Últ. Modificación</th>
                  <th onClick={() => handleSort('nombre_colegio')}>
                    Nombre Colegio
                    {sortConfig.key === 'nombre_colegio' && (
                      sortConfig.direction === 'asc' ? <i className="bi bi-arrow-up"></i> : <i className="bi bi-arrow-down"></i>
                    )}
                  </th>
                  <th>Dirección</th>
                  <th>Teléfono</th>
                  <th>Email</th>
                  <th>Representante</th>
                  <th>Cuenta CAT</th>
                  <th>Cuenta CAB</th>
                  <th>Cuenta Visado</th>
                  <th title='Tolerancia aplicada en Visados y Certificados'>Cuotas Tolerancia</th>
                  <th className='col-simple-actions'>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sortedDatGenerals.map((dat, index) => (
                  <tr key={dat.id}>
                    <td>{index + 1}</td>
                     <td>{fechaHoraBdAjustada(dat.updatedAt).completa}</td>
                    <td>{dat.nombre_colegio}</td>
                    <td>{dat.direccion}</td>
                    <td>{dat.telefono}</td>
                    <td>{dat.email}</td>
                    <td>{dat.representante}</td>
                    <td>{dat.cuenta_CAT}</td>
                    <td>{dat.cuenta_CAB}</td>
                    <td>{dat.cuenta_visado}</td>
                    <td>{dat.cuotas_tolerancia}</td>
                    <td>
                      <button
                        className="icon-button btn-secondary-outline"
                        title="Editar"
                        onClick={() => handleEdit(dat)}
                        
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="icon-button btn-secondary-outline"
                        title="Eliminar"
                        onClick={() => handleDelete(dat.id)}
                        disabled
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {showModal && (
          <DatGeneralForm datGeneral={currentDatGeneral} show={showModal} handleClose={handleClose} />
        )}
        <CustomPrintModalH
          show={showPreviewModal}
          handleClose={handleClosePreview}
          title1="LISTA DE DATOS GENERALES"
          title2=""
          content={printContent}
          copia={false}
        />
      </div>
    </div>
  );
};

export default DatGeneralList;
